import React, { useState, useEffect } from "react";
import { Form, Modal, Button } from "react-bootstrap";
import { fetchGoldRate } from "../../../utils/utils";
import "./ShowBookingProcessModal.css";

const NOTES = [500, 200, 100, 50, 20, 10, 5, 2, 1];

const inr = (value) => `₹${Math.round(Number(value) || 0).toLocaleString("en-IN")}`;
const toNumber = (text) => {
  const value = parseFloat(text);
  return Number.isFinite(value) ? value : 0;
};

// Scrolling over a focused number box silently changes the number. Dropping
// focus stops that without blocking the page from scrolling.
const numberGuards = {
  onWheel: (e) => e.currentTarget.blur(),
  onKeyDown: (e) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();
  },
};

const ShowBookingProcessModal = ({ show, handleClose, handleConfirm }) => {
  const [goldRate, setGoldRate] = useState(0);
  const [goldRateLoading, setGoldRateLoading] = useState(true);
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);

  // Kept as typed text so a box can be empty; the numbers are worked out below.
  const [fields, setFields] = useState({
    ornamentModelNo: "",
    ornamentName: "",
    ornamentWeight: "",
    wastage: "",
    gst: "",
    bookingAmount: "",
  });
  const [noteCounts, setNoteCounts] = useState({});

  // Read today's rate every time the form opens, so once the admin enters it the
  // Branch Manager only has to close and reopen the form, not reload the page.
  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    setGoldRateLoading(true);
    const getGoldRate = async () => {
      const rate = await fetchGoldRate();
      if (cancelled) return;
      setGoldRate(Number(rate) || 0);
      setGoldRateLoading(false);
    };
    getGoldRate();
    return () => {
      cancelled = true;
    };
  }, [show]);

  const setField = (e) => {
    const { name, value } = e.target;
    setFields((prev) => ({ ...prev, [name]: value }));
  };

  const setNoteCount = (note, value) => {
    const digits = value.replace(/\D/g, "");
    setNoteCounts((prev) => ({ ...prev, [note]: digits }));
  };

  // Every value is worked out from what is typed, on every change. The old form
  // only refreshed a value when its own box was edited, so changing the weight
  // after the wastage left the wastage and GST values stale.
  const ornamentWeight = toNumber(fields.ornamentWeight);
  const wastage = toNumber(fields.wastage);
  const gst = toNumber(fields.gst);
  const bookingAmount = toNumber(fields.bookingAmount);

  const ornamentWeightValue = Math.round(ornamentWeight * goldRate);
  const wastageValue = Math.round((wastage * ornamentWeightValue) / 100);
  const total = ornamentWeightValue + wastageValue;
  const gstValue = Math.round((total * gst) / 100);
  const grandTotal = total + gstValue;

  const denominations = NOTES.reduce((all, note) => {
    const count = parseInt(noteCounts[note], 10) || 0;
    all[note] = { count, subTotal: count * note };
    return all;
  }, {});
  const notesTotal = NOTES.reduce((sum, note) => sum + denominations[note].subTotal, 0);
  const notesDifference = bookingAmount - notesTotal;

  const errors = {
    ornamentModelNo: fields.ornamentModelNo.trim() ? "" : "Enter the ornament model number.",
    ornamentName: fields.ornamentName.trim() ? "" : "Enter the ornament name.",
    ornamentWeight: ornamentWeight > 0 ? "" : "Enter the ornament weight in grams.",
    wastage: wastage > 0 ? "" : "Enter the wastage percentage.",
    gst: gst > 0 ? "" : "Enter the GST percentage.",
    bookingAmount: bookingAmount > 0 ? "" : "Enter the booking amount.",
  };
  const noGoldRate = !goldRateLoading && !(goldRate > 0);
  const notesMismatch = bookingAmount > 0 && notesDifference !== 0;
  const hasErrors = Object.values(errors).some(Boolean) || noGoldRate || notesMismatch || notesTotal === 0;

  const errorFor = (name) => (showErrors ? errors[name] : "");

  const handleSave = async () => {
    setShowErrors(true);
    if (hasErrors || saving) return;

    setSaving(true);
    try {
      await handleConfirm(
        {
          ornamentModelNo: fields.ornamentModelNo.trim(),
          ornamentName: fields.ornamentName.trim(),
          ornamentWeight,
          ornamentWeightValue,
          wastage,
          wastageValue,
          total,
          gst,
          gstValue,
          grandTotal,
          bookingAmount,
        },
        denominations,
        goldRate
      );
    } finally {
      setSaving(false);
    }
  };

  const required = <span className="bkp-required">*</span>;

  const errorLine = (name) =>
    errorFor(name) ? <div className="bkp-error">{errorFor(name)}</div> : null;

  const textField = (name, label, placeholder) => (
    <div className="bkp-field">
      <label htmlFor={`bkp-${name}`}>
        {label} {required}
      </label>
      <Form.Control
        id={`bkp-${name}`}
        type="text"
        name={name}
        value={fields[name]}
        placeholder={placeholder}
        onChange={setField}
        isInvalid={!!errorFor(name)}
      />
      {errorLine(name)}
    </div>
  );

  const unitInput = (name, unit, placeholder, unitOnLeft = false) => (
    <div className={`bkp-input-unit ${unitOnLeft ? "bkp-unit-on-left" : ""}`}>
      <Form.Control
        id={`bkp-${name}`}
        type="number"
        min="0"
        step="any"
        name={name}
        value={fields[name]}
        placeholder={placeholder}
        onChange={setField}
        isInvalid={!!errorFor(name)}
        {...numberGuards}
      />
      <span className="bkp-unit">{unit}</span>
    </div>
  );

  // One invoice line: what is entered, and the amount it gives.
  const valueRow = (name, label, hint, unit, placeholder, amount) => (
    <tr>
      <th scope="row">
        <label htmlFor={`bkp-${name}`}>
          {label} {required}
        </label>
        <span className="bkp-hint">{hint}</span>
      </th>
      <td className="bkp-col-input">
        {unitInput(name, unit, placeholder)}
        {errorLine(name)}
      </td>
      <td className="bkp-num">{inr(amount)}</td>
    </tr>
  );

  const rateKnown = !goldRateLoading && !noGoldRate;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      className="bkp-modal"
      dialogClassName="bkp-dialog"
    >
      <Modal.Header closeButton>
        <Modal.Title>Booking Process</Modal.Title>
        <div className={`bkp-rate ${noGoldRate ? "bkp-rate-missing" : ""}`}>
          Gold rate today (22k)
          <strong>{goldRateLoading ? "Loading..." : noGoldRate ? "Not set" : `${inr(goldRate)} / g`}</strong>
        </div>
      </Modal.Header>
      <Modal.Body>
        {goldRateLoading && <div className="bkp-wait">Checking today&apos;s gold rate...</div>}

        {/* Without today's rate nothing on this form can be valued, so only the
            warning is shown until the admin enters the rate. */}
        {noGoldRate && (
          <div className="alert alert-danger bkp-blocked" role="alert">
            <strong>Today&apos;s gold rate is not updated.</strong>
            <span>
              The booking process cannot be done until the admin enters today&apos;s 22k gold rate
              in Daily Gold Rate. After the rate is entered, close this window and open the
              booking process again.
            </span>
          </div>
        )}

        {rateKnown && (
          <Form onSubmit={(e) => e.preventDefault()} className="bkp-layout">
            <section className="bkp-col">
              <h6 className="bkp-section">Ornament and value</h6>
              <div className="bkp-pair">
                {textField("ornamentModelNo", "Ornament model number", "e.g. CH-1024")}
                {textField("ornamentName", "Ornament name", "e.g. Chain")}
              </div>

              <table className="bkp-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="bkp-col-input">Enter</th>
                    <th className="bkp-num">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {valueRow(
                    "ornamentWeight",
                    "Ornament weight",
                    rateKnown ? `Grams × ${inr(goldRate)}` : "Grams",
                    "g",
                    "0.000",
                    ornamentWeightValue
                  )}
                  {valueRow("wastage", "Wastage", "% of gold value", "%", "0", wastageValue)}
                  <tr className="bkp-subtotal">
                    <th scope="row">Gold value + wastage</th>
                    <td />
                    <td className="bkp-num">{inr(total)}</td>
                  </tr>
                  {valueRow("gst", "GST", "% of the line above", "%", "0", gstValue)}
                </tbody>
                <tfoot>
                  <tr className="bkp-grand">
                    <th scope="row">Grand total</th>
                    <td />
                    <td className="bkp-num">{inr(grandTotal)}</td>
                  </tr>
                </tfoot>
              </table>
            </section>

            <section className="bkp-col bkp-col-right">
              <h6 className="bkp-section">Booking amount received</h6>
              <div className="bkp-field">
                <label htmlFor="bkp-bookingAmount">Booking amount {required}</label>
                {unitInput("bookingAmount", "₹", "0", true)}
                {errorLine("bookingAmount")}
              </div>

              <table className="bkp-table bkp-notes">
                <thead>
                  <tr>
                    <th>Notes received {required}</th>
                    <th className="bkp-col-count">Count</th>
                    <th className="bkp-num">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {NOTES.map((note) => (
                    <tr key={note}>
                      <th scope="row">₹{note}</th>
                      <td className="bkp-col-count">
                        <Form.Control
                          type="text"
                          inputMode="numeric"
                          value={noteCounts[note] || ""}
                          placeholder="0"
                          aria-label={`Number of ${note} rupee notes`}
                          onChange={(e) => setNoteCount(note, e.target.value)}
                        />
                      </td>
                      <td className="bkp-num">
                        {denominations[note].subTotal ? inr(denominations[note].subTotal) : "–"}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bkp-grand">
                    <th scope="row">Total counted</th>
                    <td />
                    <td className="bkp-num">{inr(notesTotal)}</td>
                  </tr>
                </tfoot>
              </table>

              {bookingAmount > 0 && (
                <div className={`bkp-match ${notesDifference === 0 ? "bkp-match-ok" : "bkp-match-bad"}`}>
                  {notesDifference === 0 && "The notes match the booking amount."}
                  {notesDifference > 0 && `The notes are ${inr(notesDifference)} less than the booking amount.`}
                  {notesDifference < 0 && `The notes are ${inr(-notesDifference)} more than the booking amount.`}
                </div>
              )}
              {showErrors && bookingAmount > 0 && notesTotal === 0 && (
                <div className="bkp-error">Enter how many of each note was received.</div>
              )}
            </section>
          </Form>
        )}

        {rateKnown && showErrors && hasErrors && (
          <div className="alert alert-danger bkp-alert bkp-alert-bottom">
            Some details are missing or do not match. Please check the fields marked in red.
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {rateKnown && <span className="bkp-legend">{required} Mandatory field</span>}
        <Button variant="secondary" onClick={handleClose} disabled={saving}>
          {rateKnown ? "Cancel" : "Close"}
        </Button>
        {rateKnown && (
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save and print receipt"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ShowBookingProcessModal;
